import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Settings2, Loader2, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { TemplateAccessNote } from "@/components/TemplateAccessNote";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/ops/requested/config`;
const fill = (t, f) => t.replace(/\{(\w+)\}/g, (m, k) => (k in f ? String(f[k] ?? "") : m));

// Requested → per-location boarding confirmation template.
export const RequestedMessageConfig = ({ onSaved }) => {
  const [open, setOpen] = useState(false);
  const [cfg, setCfg] = useState(null);
  const [tpl, setTpl] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!open) return;
    axios.get(API).then((r) => { setCfg(r.data); setTpl(r.data.template || ""); }).catch(() => toast.error("Could not load template"));
  }, [open]);
  const save = async () => {
    setSaving(true);
    try {
      await axios.put(API, { template: tpl });
      toast.success("Template saved — new drafts use it"); setOpen(false); onSaved?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); } finally { setSaving(false); }
  };
  const preview = cfg ? (tpl.trim() ? fill(tpl.trim(), cfg.sample_fields) : cfg.default_preview) : "";
  const over = cfg && preview.length > cfg.max;
  return (
    <>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)} data-testid="req-msg-config-btn"><Settings2 className="w-4 h-4 mr-1.5" />Message template</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="ops-v3 max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="req-msg-config-dialog">
          <DialogHeader>
            <DialogTitle>Boarding request message</DialogTitle>
            <DialogDescription>For this location only. Leave blank to use the standard confirmation text. If a filled-in message is over {cfg?.max || 320} characters, that request falls back to the standard text.</DialogDescription>
          </DialogHeader>
          {!cfg ? <Loader2 className="w-5 h-5 animate-spin text-gray-400" /> : (
            <div className="space-y-3">
              <TemplateAccessNote canEdit={cfg.can_edit} testid="req-tpl-access" />
              <div className="text-xs text-gray-500">Fields you can use: {cfg.fields.map((f) => <code key={f} className="mr-1.5 bg-gray-100 rounded px-1">{`{${f}}`}</code>)}</div>
              <div className="flex justify-end">{tpl && cfg.can_edit && <button className="text-xs text-indigo-600 inline-flex items-center gap-1" onClick={() => setTpl("")} data-testid="req-tpl-reset"><RotateCcw className="w-3 h-3" />Use standard text</button>}</div>
              <Textarea rows={5} value={tpl} disabled={!cfg.can_edit} onChange={(e) => setTpl(e.target.value)} placeholder="Leave blank to use the standard confirmation (shown below)" data-testid="req-tpl-input" />
              <div className={`text-xs rounded-lg p-2 ${tpl ? "bg-indigo-50 text-indigo-900" : "bg-gray-50 text-gray-600"}`} data-testid="req-tpl-preview">
                <span className="font-bold">{tpl ? "Preview (2 dogs, sample stay)" : "Standard text"}:</span> {preview}
                <span className={`ml-2 font-bold ${over ? "text-rose-600" : "text-gray-400"}`} data-testid="req-tpl-count">{preview.length}/{cfg.max}{over ? " — too long, will fall back" : ""}</span>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
                <Button onClick={save} disabled={saving || !cfg.can_edit} data-testid="req-msg-config-save">{saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Save</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
